import type {
  Browser,
  CursorPackPayload,
  ExtensionConfig,
  ExtensionRequest,
  ExtensionResult,
  ExtensionStatus,
  InstalledCursors,
  NewUploadedCursor,
  UploadedCursor,
} from "./client.types"
import { ExtensionConnection } from "./connection"

export class ExtensionClient {
  static #connection: typeof ExtensionConnection = ExtensionConnection
  static #installedCursorPacks: number[] | null = null
  static #installedCursorPacksRequest: Promise<number[]> | null = null

  static initialize(config: ExtensionConfig): void {
    if (!this.connectionNotInitialized) return

    this.#connection.setup(config)
    // Drop the cache in case the connection changed.
    this.#connection.onChange(() => {
      this.#installedCursorPacks = null
      this.#installedCursorPacksRequest = null
    })
  }

  static onConnectionChange(callback: () => void): () => void {
    return this.#connection.onChange(callback)
  }

  static get connectionNotInitialized() {
    return (
      this.#connection.status === "uninitialized" && this.#connection.connectionAttempt === null
    )
  }

  static get status(): ExtensionStatus {
    return this.#connection.status
  }

  static get notConnected() {
    return this.#connection.status === "not_connected"
  }

  static get notSupported() {
    return this.#connection.status === "unsupported"
  }

  static get storeUrl(): string | null {
    return this.#connection.storeUrl
  }

  static get browser(): Browser | null {
    return this.#connection.browser
  }

  static installedCursorPacks(): Promise<number[]> {
    if (this.#installedCursorPacks) return Promise.resolve(this.#installedCursorPacks)

    return (this.#installedCursorPacksRequest ??= this.#fetchInstalledCursorPacks())
  }

  static async isCursorPackAdded(id: number) {
    return (await this.installedCursorPacks()).includes(id)
  }

  static async addCursorPack(pack: CursorPackPayload): Promise<boolean> {
    if (this.connectionNotInitialized) {
      throw new Error("ExtensionClient.initialize() must run before the client is used")
    }

    const result = await this.#connection.sendMessage({
      action: "addCursorConfig",
      collection: pack.collection,
      cursorConfig: pack.cursorConfig,
    })
    if (!result.success) {
      console.error(
        `[ExtensionClient] Failed to add the cursor pack. Response: ${JSON.stringify(result)}`,
      )
      return false
    }

    this.#installedCursorPacks = [...(this.#installedCursorPacks ?? []), pack.cursorConfig.id]
    return true
  }

  // Newest first; null when the extension refused or didn't answer.
  static async uploadedCursors(): Promise<UploadedCursor[] | null> {
    const result = await this.#sendUploadedCollectionMessage<{ cursors: UploadedCursor[] }>(
      { action: "UploadedCollectionApi.getCursors" },
      "load the uploaded cursors",
    )
    return result.success ? result.data.cursors : null
  }

  // Resolves to the pack as the extension stored it, or null when it refused or didn't answer.
  static async addUploadedCursor(newCursor: NewUploadedCursor): Promise<UploadedCursor | null> {
    const result = await this.#sendUploadedCollectionMessage<{ cursor: UploadedCursor }>(
      { action: "UploadedCollectionApi.addCursor", newCursor },
      "add the uploaded cursor",
    )
    return result.success ? result.data.cursor : null
  }

  // Resolves to the pack as the extension stored it, or null when it refused or didn't answer.
  static async updateUploadedCursor(
    cursorId: number,
    cursor: NewUploadedCursor,
  ): Promise<UploadedCursor | null> {
    // The extension replaces the whole pack, so the favorite mark its popup sets is read back and
    // sent along. Read fresh: the visitor may have set it after the page loaded its packs.
    const stored = (await this.uploadedCursors())?.find(({ id }) => id === cursorId)
    const result = await this.#sendUploadedCollectionMessage<{ cursor: UploadedCursor }>(
      {
        action: "UploadedCollectionApi.updateCursor",
        cursorId,
        cursor: { ...cursor, isFavorite: stored?.isFavorite },
      },
      "update the uploaded cursor",
    )
    return result.success ? result.data.cursor : null
  }

  static async removeUploadedCursor(cursorId: number): Promise<boolean> {
    const result = await this.#sendUploadedCollectionMessage<null>(
      { action: "UploadedCollectionApi.removeCursor", cursorId },
      "remove the uploaded cursor",
    )
    return result.success
  }

  static async #sendUploadedCollectionMessage<ResponseData>(
    message: ExtensionRequest,
    failedAction: string,
  ): Promise<ExtensionResult<ResponseData>> {
    if (this.connectionNotInitialized) {
      throw new Error("ExtensionClient.initialize() must run before the client is used")
    }

    const result = await this.#connection.sendMessage<ResponseData>(message)
    if (!result.success) {
      console.error(
        `[ExtensionClient] Failed to ${failedAction}. Response: ${JSON.stringify(result)}`,
      )
    }

    return result
  }

  static async #fetchInstalledCursorPacks(): Promise<number[]> {
    try {
      if (this.connectionNotInitialized) return []

      const result = await this.#connection.sendMessage<InstalledCursors>({
        action: "getInstalledCursors",
      })
      // Cache only on success; a failure stays uncached so a later call retries.
      if (result.success) {
        this.#installedCursorPacks = result.data.cursorIds
        return result.data.cursorIds
      }

      return []
    } finally {
      this.#installedCursorPacksRequest = null
    }
  }
}
